import logoUrl from '../../assets/vello_logo.svg';
import './LoginPage.scss';
import { useTranslation } from 'react-i18next';
import Input from '../../components/input/Input.tsx';
import Button from '../../components/button/Button.tsx';
import { useLogin, useNotify, useRedirect } from 'react-admin';
import { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { MemberService } from "../../services/member/memberService.ts";
import {
    signInWithEmailAndPassword,
} from 'firebase/auth';
import { auth } from "../../core/firebaseConfig.ts";
import * as Sentry from '@sentry/react';
import ShabbatService from '../../services/shabbat/shabbatService.ts';
import { isShabbatOnlyLogin, rememberShabbatOnly } from '../../core/shabbatOnly.ts';


export default function LoginPage() {
    const login = useLogin();
    const notify = useNotify();
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const memberService = new MemberService();

    const validationSchema = Yup.object({
        email: Yup.string().email('Invalid email').required('Required'),
        password: Yup.string().required('Required'),
    });

    return (
        <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={validationSchema}
            validateOnBlur
            validateOnChange
            onSubmit={async (values, { setSubmitting }) => {
                setLoading(true);
                try {
                    const userCredential = await signInWithEmailAndPassword(auth, values.email, values.password);
                    const { user } = userCredential;
                    const token = await user.getIdToken();
                    try {
                        const response = await memberService.signIn(userCredential.user.uid);
                        if (response?.isBlocked || response?.isDeleted || !response?.roles?.some((role: string) => role === "ADMIN" || role === "SUPER_ADMIN" || role === "VENDOR")) {
                            console.log(response?.roles);
                            notify('There was an error during log in', { type: 'error' });
                            return;
                        }
                        localStorage.setItem('ra_role', response?.roles);
                        await login({ uid: userCredential.user.uid, token }, '/');
                        // Before the reload, so the panel opens in the right
                        // shape: a Chabad House login gets the Shabbat pages only.
                        if (response.roles.includes('VENDOR')) {
                            try {
                                rememberShabbatOnly(await new ShabbatService().venues());
                            } catch { /* the full panel; the menu asks again */ }
                        }
                        // While that was asked, react-admin went on to the first
                        // page of the panel as it stood before sign-in (Members,
                        // which a restaurant does not have). Start from the top.
                        window.location.hash = isShabbatOnlyLogin() ? '#/shabbat/meals' : '#/';
                        window.location.reload();
                    } catch (e: any) {
                        console.error(e)
                        Sentry.captureException(e);
                    }
                } catch (e: any) {
                    console.error(e)
                    notify(e?.message || 'login.failed', { type: 'error' });
                    Sentry.captureException(e);
                } finally {
                    setLoading(false);
                    setSubmitting(false);
                }
            }}
        >
            {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                <Form className="login login--centered">
                    <div className="login__card login__card--elevated">
                        <img className="login__logo" src={logoUrl} alt="Vello logo" />
                        <div>
                            <p className="login__title">{t('login.logInToYourAccount')}</p>
                            <p className="login__subtitle">{t('login.welcomeBack')}</p>
                        </div>
                        <div className="login__form">
                            <Input
                                name="email"
                                label={t('login.email')}
                                type="email"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.email}
                                autoComplete="email"
                                error={Boolean(touched.email && errors.email)}
                                helperText={touched.email ? errors.email : undefined}
                                aria-invalid={Boolean(touched.email && errors.email)}
                                aria-describedby="email-error"
                            />
                            <Input
                                name="password"
                                label={t('login.password')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                type="password"
                                value={values.password}
                                autoComplete="current-password"
                                error={Boolean(touched.password && errors.password)}
                                helperText={touched.password ? errors.password : undefined}
                                aria-invalid={Boolean(touched.password && errors.password)}
                                aria-describedby="password-error"
                            />
                            <Button text={t('login.logIn')} type="submit" disabled={isSubmitting || loading} />
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );
};